import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContentPerformanceTable = ({ articles, onViewDetails }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'publishedDate', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedArticles = [...articles]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const totalPages = Math.ceil(sortedArticles?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedArticles = sortedArticles?.slice(startIndex, startIndex + itemsPerPage);

  const getStatusColor = (status) => {
    switch (status) {
      case 'published':
        return 'bg-success/10 text-success';
      case 'scheduled':
        return 'bg-primary/10 text-primary';
      case 'failed':
        return 'bg-error/10 text-error';
      case 'draft':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getSEOScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">Content Performance Analytics</h3>
        <p className="text-sm text-muted-foreground mt-1">Article-level engagement metrics and automation status</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('title')}
                  className="flex items-center gap-2 text-xs font-medium text-foreground hover:text-primary transition-colors"
                >
                  Article Title
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('site')}
                  className="flex items-center gap-2 text-xs font-medium text-foreground hover:text-primary transition-colors"
                >
                  Site
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('publishedDate')}
                  className="flex items-center gap-2 text-xs font-medium text-foreground hover:text-primary transition-colors"
                >
                  Published
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-center">
                <button
                  onClick={() => handleSort('views')}
                  className="flex items-center gap-2 text-xs font-medium text-foreground hover:text-primary transition-colors mx-auto"
                >
                  Views
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-center">
                <button
                  onClick={() => handleSort('engagement')}
                  className="flex items-center gap-2 text-xs font-medium text-foreground hover:text-primary transition-colors mx-auto"
                >
                  Engagement
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-center">
                <button
                  onClick={() => handleSort('seoScore')}
                  className="flex items-center gap-2 text-xs font-medium text-foreground hover:text-primary transition-colors mx-auto"
                >
                  SEO Score
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-foreground">Status</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedArticles?.map((article) => (
              <tr key={article?.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{article?.title}</p>
                      <p className="text-xs text-muted-foreground mt-1">{article?.category}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm text-foreground">{article?.site}</p>
                </td>
                <td className="px-6 py-4">
                  <p className="text-sm text-foreground">{article?.publishedDate}</p>
                  <p className="text-xs text-muted-foreground">{article?.publishedTime}</p>
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Icon name="Eye" size={14} className="text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">{article?.views}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Icon name="Heart" size={14} className="text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">{article?.engagement}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`text-sm font-semibold ${getSEOScoreColor(article?.seoScore)}`}>
                    {article?.seoScore}/100
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(article?.status)}`}>
                    {article?.automationType && (
                      <Icon name="Zap" size={12} />
                    )}
                    {article?.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-center">
                  <Button
                    variant="ghost"
                    size="xs"
                    iconName="ExternalLink"
                    onClick={() => onViewDetails(article?.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-6 py-4 border-t border-border">
        <p className="text-sm text-muted-foreground">
          Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, sortedArticles?.length)} of {sortedArticles?.length} articles
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronLeft"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </Button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                    currentPage === pageNum
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronRight"
            iconPosition="right"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContentPerformanceTable;